import { Controller } from 'stimulus'

// Schedule settings: the working-day buttons keep the hidden checkbox beside
// each one in step (UI style guide, Day buttons), and the People and
// Equipment tabs post only the rows someone changed. A large account's whole
// equipment table would pass Rack's 4,096-parameter limit. Turning
// Automation on asks first (the switch's data-confirm-on says how many tasks
// the planner will place).
//
// The save bar (UI style guide, Save bar): it stays on screen at the bottom
// of the window, says how many changes are waiting (a changed table row, or
// a changed control on General, counts one), marks each changed one in amber,
// and Discard Changes puts the tab back as saved. Leaving with changes
// waiting asks first, as the role editor does: another tab, Back to the
// Schedule or the menu (a Turbolinks visit), or closing the browser tab.
// (The browser's Back restores Turbolinks' cached copy without asking.)
//
// "As saved" is what the server rendered (each input's default), not what
// the inputs hold when the page connects: a page restored from a cache
// still holds the edits it was left with, and they must read as unsaved.
export default class extends Controller {
  static targets = ['row', 'mark', 'automation', 'tabDot', 'savebar', 'sentinel', 'state', 'save', 'discard'];

  connect() {
    if (this.hasAutomationTarget) this.automationWasOn = this.automationTarget.defaultChecked
    this.leaving = false

    this.onBeforeUnload = (event) => {
      // (an idle sign-out leaves without asking: session_timeout_controller)
      if (this.leaving || window.limseySigningOut || !this.count()) return
      const unloadEvent = event
      unloadEvent.preventDefault()
      unloadEvent.returnValue = ''
    }
    this.onBeforeVisit = (event) => {
      if (this.leaving) return
      const waiting = this.count()
      if (waiting && !window.confirm(`You have ${this.changesLabel(waiting)} to Schedule settings. Leave without saving?`)) {
        event.preventDefault()
      }
    }
    this.onEdit = () => this.refresh()
    // Back to a page the browser kept whole after a save: the save left the
    // unchanged rows disabled and the guard off
    this.onPageShow = (event) => {
      if (!event.persisted) return
      this.leaving = false
      this.element.querySelectorAll('[data-ss-off]').forEach((input) => {
        input.removeAttribute('disabled')
        input.removeAttribute('data-ss-off')
      })
      this.syncDays()
      this.refresh()
    }
    window.addEventListener('beforeunload', this.onBeforeUnload)
    window.addEventListener('pageshow', this.onPageShow)
    document.addEventListener('turbolinks:before-visit', this.onBeforeVisit)
    this.element.addEventListener('input', this.onEdit)
    this.element.addEventListener('change', this.onEdit)
    this.observeSavebar()
    this.refresh()
  }

  disconnect() {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
    window.removeEventListener('pageshow', this.onPageShow)
    document.removeEventListener('turbolinks:before-visit', this.onBeforeVisit)
    this.element.removeEventListener('input', this.onEdit)
    this.element.removeEventListener('change', this.onEdit)
    if (this.savebarObserver) this.savebarObserver.disconnect()
  }

  toggleDay(event) {
    const button = event.currentTarget
    const on = button.getAttribute('aria-pressed') !== 'true'
    button.setAttribute('aria-pressed', on.toString())
    const box = button.nextElementSibling
    if (box && box.type === 'checkbox') box.checked = on
    this.refresh()
  }

  // Back to what's saved: the form's own defaults, then the day buttons
  // from their checkboxes
  discard() {
    const form = this.element.querySelector('form')
    if (form) form.reset()
    this.syncDays()
    this.refresh()
  }

  syncDays() {
    this.element.querySelectorAll('.ui-day').forEach((button) => {
      const box = button.nextElementSibling
      if (box && box.type === 'checkbox') button.setAttribute('aria-pressed', box.checked.toString())
    })
  }

  // Runs before postChangedRows, so a "no" leaves the form as it was
  confirmAutomation(event) {
    if (!this.hasAutomationTarget || this.automationWasOn || !this.automationTarget.checked) return

    const question = this.automationTarget.dataset.confirmOn
    if (question && !window.confirm(question)) {
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  }

  postChangedRows() {
    this.rowTargets.forEach((row) => {
      if (this.changed(row)) return
      row.querySelectorAll('input:not([disabled]), select:not([disabled])').forEach((input) => {
        input.setAttribute('disabled', 'disabled')
        input.setAttribute('data-ss-off', '')
      })
    })
  }

  // The last submit action: saving isn't leaving
  allowLeave() {
    this.leaving = true
  }

  refresh() {
    let waiting = 0
    this.units().forEach((unit) => {
      const changed = this.changed(unit)
      unit.classList.toggle('is-chg', changed)
      if (changed) waiting += 1
    })
    if (this.hasStateTarget) {
      this.stateTarget.classList.toggle('is-dirty', waiting > 0)
      this.stateTarget.textContent = waiting ? this.changesLabel(waiting) : 'No changes'
    }
    if (this.hasSaveTarget) this.saveTarget.disabled = waiting === 0
    if (this.hasDiscardTarget) this.discardTarget.hidden = waiting === 0
    if (this.hasTabDotTarget) this.tabDotTarget.hidden = waiting === 0
  }

  // The bar is sticky; while its resting place (the sentinel just after it)
  // is out of view it floats over the rows and gets a shadow
  observeSavebar() {
    if (!this.hasSavebarTarget || !this.hasSentinelTarget || !('IntersectionObserver' in window)) return

    this.savebarObserver = new IntersectionObserver((entries) => {
      this.savebarTarget.classList.toggle('is-floating', !entries[0].isIntersecting)
    }, { rootMargin: '0px 0px -24px 0px' })
    this.savebarObserver.observe(this.sentinelTarget)
  }

  count() {
    return this.units().filter(unit => this.changed(unit)).length
  }

  changed(unit) {
    return this.state(unit) !== this.state(unit, true)
  }

  changesLabel(waiting) {
    return `${waiting} unsaved ${waiting === 1 ? 'change' : 'changes'}`
  }

  units() {
    return this.rowTargets.concat(this.markTargets)
  }

  // saved: what the server rendered (the inputs' defaults), else what they hold
  state(unit, saved = false) {
    return Array.from(unit.querySelectorAll('input, select'))
      .map((input) => {
        if (input.type === 'checkbox') return saved ? input.defaultChecked : input.checked
        if (input.tagName === 'SELECT') {
          if (!saved) return input.value
          const option = Array.from(input.options).find(o => o.defaultSelected) || input.options[0]
          return option ? option.value : ''
        }
        return saved ? input.defaultValue : input.value
      })
      .join('|')
  }
}
